if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();
      this.content = this.querySelector('[role="document"]');
      this.mediaItems = Array.from(this.content.children).filter(item => item.hasAttribute('data-media-id'));
      this.currentIndex = 0;
      this.swipeStart = null;
      this.classList.add('rr-gallery-navigation');
      const navigation = document.createElement('div');
      navigation.className = 'rr-gallery-controls';
      navigation.setAttribute('role', 'group');
      navigation.setAttribute('aria-label', 'Product media navigation');
      this.previousButton = this.makeButton('Previous product media', 'previous', -1);
      this.nextButton = this.makeButton('Next product media', 'next', 1);
      this.counter = document.createElement('span');
      this.counter.className = 'rr-gallery-counter';
      this.counter.setAttribute('role', 'status');
      this.counter.setAttribute('aria-live', 'polite');
      navigation.append(this.previousButton, this.counter, this.nextButton);
      // Dawn closes a media modal on mouse pointerup; navigation must stay open.
      navigation.addEventListener('pointerup', event => event.stopPropagation());
      this.thumbnails = document.createElement('div');
      this.thumbnails.className = 'rr-gallery-thumbnails';
      this.thumbnails.setAttribute('role', 'group');
      this.thumbnails.setAttribute('aria-label', 'Choose product image');
      this.thumbnailButtons = this.mediaItems.map((item, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-label', `View product media ${index + 1}`);
        const source = item.matches('img') ? item : item.querySelector('img');
        if (source) {
          const image = document.createElement('img');
          const url = new URL(source.currentSrc || source.src, window.location.href);
          url.searchParams.set('width', '120');
          image.src = url.href;
          image.alt = '';
          image.loading = 'lazy';
          button.append(image);
        } else button.textContent = String(index + 1);
        if (item.nodeName === 'DEFERRED-MEDIA') button.setAttribute('aria-label', `View product video ${index + 1}`);
        button.addEventListener('click', () => this.showMedia(index));
        this.thumbnails.append(button);
        return button;
      });
      this.thumbnails.addEventListener('pointerup', event => event.stopPropagation());
      this.loader = document.createElement('div');
      this.loader.className = 'rr-gallery-loader';
      this.loader.setAttribute('role', 'status');
      this.loader.setAttribute('aria-label', 'Loading image');
      this.loader.hidden = true;
      this.loader.innerHTML = '<span aria-hidden="true"></span>';
      this.querySelector('[role="dialog"]').append(navigation, this.thumbnails, this.loader);
      this.addEventListener('keydown', event => {
        if (!this.hasAttribute('open') || event.target.closest('input, textarea, select, video, iframe, model-viewer')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          event.stopPropagation();
          this.showMedia(this.currentIndex + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      this.content.addEventListener('pointerdown', event => {
        this.swipeStart = null;
        if (!event.isPrimary || !['touch', 'pen'].includes(event.pointerType) ||
            event.target.closest('button, a, input, select, textarea, deferred-media, video, iframe, model-viewer') ||
            (window.visualViewport && window.visualViewport.scale > 1.05)) return;
        this.swipeStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
      }, { passive: true });
      this.content.addEventListener('pointercancel', () => { this.swipeStart = null; });
      this.content.addEventListener('pointerup', event => {
        const start = this.swipeStart;
        this.swipeStart = null;
        if (!start || start.id !== event.pointerId || (window.visualViewport && window.visualViewport.scale > 1.05)) return;
        const dx = event.clientX - start.x, dy = event.clientY - start.y;
        if (Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy) * 1.35) {
          this.showMedia(this.currentIndex + (dx < 0 ? 1 : -1));
        }
      }, { passive: true });
    }
    makeButton(label, direction, delta) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `rr-gallery-arrow rr-gallery-arrow--${direction}`;
      button.setAttribute('aria-label', label);
      button.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${delta < 0 ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'}" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      button.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        this.showMedia(this.currentIndex + delta);
      });
      return button;
    }
    hide() {
      this.swipeStart = null;
      this.loadSequence = (this.loadSequence || 0) + 1;
      this.loader.hidden = true;
      super.hide();
    }
    show(opener) {
      super.show(opener);
      this.showActiveMedia();
    }
    showActiveMedia() {
      const index = this.mediaItems.findIndex(item => item.dataset.mediaId === this.openedBy.getAttribute('data-media-id'));
      this.showMedia(index < 0 ? 0 : index);
    }
    showMedia(index) {
      if (!this.mediaItems.length) return;
      const next = Math.max(0, Math.min(index, this.mediaItems.length - 1));
      if (window.pauseAllMedia) window.pauseAllMedia();
      this.currentIndex = next;
      const sequence = this.loadSequence = (this.loadSequence || 0) + 1;
      this.thumbnailButtons.forEach((button, i) => button.setAttribute('aria-current', i === next ? 'true' : 'false'));
      this.thumbnailButtons[next].scrollIntoView({ block: 'nearest', inline: 'nearest' });
      this.mediaItems.forEach((item, i) => item.classList.toggle('active', i === next));
      const active = this.mediaItems[next];
      const finishLoading = () => {
        if (this.loadSequence !== sequence) return;
        this.loader.hidden = true;
        active.classList.remove('rr-image-loading');
        this.content.setAttribute('aria-busy', 'false');
      };
      this.mediaItems.forEach(item => item.classList.remove('rr-image-loading'));
      this.loader.hidden = true;
      this.content.setAttribute('aria-busy', 'false');
      if (active.matches('img') && !(active.complete && active.naturalWidth)) {
        this.loader.hidden = false;
        active.classList.add('rr-image-loading');
        this.content.setAttribute('aria-busy', 'true');
        active.addEventListener('load', finishLoading, { once: true });
        active.addEventListener('error', finishLoading, { once: true });
        active.loading = 'eager';
        if (active.complete) finishLoading();
      }
      // Warm the adjacent photo cache without downloading the entire gallery.
      [this.mediaItems[next - 1], this.mediaItems[next + 1]].forEach(item => {
        if (item?.matches('img')) item.loading = 'eager';
      });
      this.content.scrollTop = 0;
      this.content.scrollLeft = 0;
      const focusedControl = document.activeElement;
      this.previousButton.disabled = next === 0;
      this.nextButton.disabled = next === this.mediaItems.length - 1;
      this.previousButton.hidden = this.nextButton.hidden = this.mediaItems.length < 2;
      this.counter.textContent = `${next + 1} of ${this.mediaItems.length}`;
      if ((focusedControl === this.previousButton && this.previousButton.disabled) ||
          (focusedControl === this.nextButton && this.nextButton.disabled)) {
        this.querySelector('[role="dialog"]').focus();
      }
      if (active.nodeName === 'DEFERRED-MEDIA' && active.querySelector('template')?.content.querySelector('.js-youtube')) active.loadContent();
    }
  });
}
